import React, { useEffect, useRef, useState } from "react";
import {
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Shuffle,
  Music2,
  Volume2,
  VolumeX,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const PLAYLIST_URL =
  "https://soundcloud.com/anthony-butler-20806238/sets/best-of-underground";
const DEFAULT_VOLUME = 22;

// Fisher-Yates shuffle of indices [0..n-1]
const shuffledOrder = (n) => {
  const arr = Array.from({ length: n }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const MusicPlayer = () => {
  const iframeRef = useRef(null);
  const widgetRef = useRef(null);
  const orderRef = useRef([]);
  const posRef = useRef(0);
  const startedRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [track, setTrack] = useState({ title: "", artist: "", artwork: "" });
  const [levels, setLevels] = useState([30, 55, 40, 65, 35]);

  // Load the SoundCloud Widget API script then init the widget
  useEffect(() => {
    let cancelled = false;

    const init = () => {
      if (cancelled || !iframeRef.current || !window.SC) return;
      const widget = window.SC.Widget(iframeRef.current);
      widgetRef.current = widget;
      const Events = window.SC.Widget.Events;

      widget.bind(Events.READY, () => {
        widget.setVolume(DEFAULT_VOLUME);
        widget.getSounds((sounds) => {
          const n = sounds && sounds.length ? sounds.length : 1;
          orderRef.current = shuffledOrder(n);
          posRef.current = 0;
          setReady(true);
          // Attempt autoplay (will likely be blocked until interaction)
          widget.skip(orderRef.current[0]);
        });
      });

      widget.bind(Events.PLAY, () => {
        startedRef.current = true;
        setPlaying(true);
        widget.getCurrentSound((s) => {
          if (!s) return;
          setTrack({
            title: s.title || "Unknown track",
            artist: (s.user && s.user.username) || "",
            artwork: (s.artwork_url || (s.user && s.user.avatar_url) || "").replace(
              "-large",
              "-t120x120"
            ),
          });
        });
      });

      widget.bind(Events.PAUSE, () => setPlaying(false));
      widget.bind(Events.FINISH, () => goNext());
    };

    if (window.SC && window.SC.Widget) {
      init();
    } else {
      const existing = document.getElementById("sc-widget-api");
      if (existing) {
        existing.addEventListener("load", init);
      } else {
        const script = document.createElement("script");
        script.id = "sc-widget-api";
        script.src = "https://w.soundcloud.com/player/api.js";
        script.async = true;
        script.onload = init;
        document.body.appendChild(script);
      }
    }

    return () => {
      cancelled = true;
    };
  }, []);

  // Start playback on the first user interaction (autoplay policy workaround)
  useEffect(() => {
    const startOnInteract = () => {
      if (widgetRef.current && !startedRef.current) {
        widgetRef.current.play();
      }
    };
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, startOnInteract, { once: false }));
    return () => events.forEach((e) => window.removeEventListener(e, startOnInteract));
  }, []);

  // Drive the equalizer bars with a lively, beat-like rhythm while playing.
  // (True frequency analysis isn't possible on cross-origin SoundCloud audio.)
  useEffect(() => {
    if (!playing) {
      setLevels((prev) => prev.map(() => 12));
      return;
    }
    const bars = 5;
    let frame = 0;
    const id = setInterval(() => {
      frame += 1;
      // a recurring "kick" every few ticks makes bars jump together like a beat
      const kick = frame % 4 === 0 ? 1 : 0;
      setLevels(
        Array.from({ length: bars }, (_, i) => {
          const base = 25 + Math.random() * 70;
          const beat = kick ? 90 + Math.random() * 10 : base;
          // center bars react a touch stronger
          const center = i === 2 ? 8 : 0;
          return Math.min(100, Math.max(12, beat + center));
        })
      );
    }, 130);
    return () => clearInterval(id);
  }, [playing]);

  const goNext = () => {
    const w = widgetRef.current;
    const order = orderRef.current;
    if (!w || !order.length) return;
    posRef.current = (posRef.current + 1) % order.length;
    w.skip(order[posRef.current]);
    w.play();
  };

  const goPrev = () => {
    const w = widgetRef.current;
    const order = orderRef.current;
    if (!w || !order.length) return;
    posRef.current = (posRef.current - 1 + order.length) % order.length;
    w.skip(order[posRef.current]);
    w.play();
  };

  const togglePlay = () => {
    const w = widgetRef.current;
    if (!w) return;
    startedRef.current = true;
    if (playing) w.pause();
    else w.play();
  };

  const toggleMute = () => {
    const w = widgetRef.current;
    if (!w) return;
    const next = !muted;
    setMuted(next);
    w.setVolume(next ? 0 : DEFAULT_VOLUME);
  };

  const longTitle = (track.title || "").length > 22;

  return (
    <>
      {/* Hidden SoundCloud widget that actually plays the audio */}
      <iframe
        ref={iframeRef}
        title="background-music"
        allow="autoplay"
        style={{ position: "fixed", width: 1, height: 1, left: -9999, bottom: 0, opacity: 0, pointerEvents: "none" }}
        scrolling="no"
        frameBorder="no"
        src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(
          PLAYLIST_URL
        )}&color=%23facc15&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false&visual=false`}
      />

      {/* Spotify-style now-playing overlay */}
      <div className="fixed bottom-4 left-4 z-[60] select-none">
        {collapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            className="w-12 h-12 rounded-full bg-yellow-400 text-[#0a101e] flex items-center justify-center shadow-xl glow-yellow"
            aria-label="Open music player"
          >
            <Music2 className={`w-5 h-5 ${playing ? "animate-spin-slow" : ""}`} />
          </button>
        ) : (
          <div className="w-[320px] bg-[#0f1a30]/95 backdrop-blur border border-yellow-400/30 rounded-2xl p-3 shadow-2xl">
            <div className="flex items-center gap-3">
              {/* Artwork */}
              <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-[#0a101e] border border-white/10 shrink-0 flex items-center justify-center">
                {track.artwork ? (
                  <img src={track.artwork} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Music2 className="w-5 h-5 text-yellow-400" />
                )}
                {playing && (
                  <span className="absolute inset-0 flex items-end justify-center gap-[2px] bg-black/30 pb-1 px-1">
                    {levels.map((h, i) => (
                      <span
                        key={i}
                        style={{
                          height: `${h}%`,
                          width: "3px",
                          background: "#facc15",
                          borderRadius: "2px",
                          transition: "height 110ms ease-out",
                        }}
                      />
                    ))}
                  </span>
                )}
              </div>

              {/* Track info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-[10px] text-yellow-400/80 tracking-widest uppercase">
                  <Shuffle className="w-3 h-3" /> Now Playing
                </div>
                <div className="marquee-mask mt-0.5">
                  {longTitle ? (
                    <div className="marquee-track text-sm text-slate-100 font-medium">
                      <span className="pr-8">{track.title}</span>
                      <span className="pr-8">{track.title}</span>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-100 font-medium truncate">
                      {ready ? track.title || "Loading\u2026" : "Loading\u2026"}
                    </div>
                  )}
                </div>
                <div className="text-xs text-slate-400 truncate">{track.artist || "best of underground"}</div>
              </div>

              {/* Collapse */}
              <button
                onClick={() => setCollapsed(true)}
                className="text-slate-400 hover:text-yellow-400 transition-colors shrink-0"
                aria-label="Minimize"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between mt-3 px-1">
              <button onClick={toggleMute} className="text-slate-400 hover:text-yellow-400 transition-colors" aria-label="Mute">
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>

              <div className="flex items-center gap-4">
                <button onClick={goPrev} disabled={!ready} className="text-slate-200 hover:text-yellow-400 transition-colors disabled:opacity-40" aria-label="Previous">
                  <SkipBack className="w-5 h-5 fill-current" />
                </button>
                <button
                  onClick={togglePlay}
                  disabled={!ready}
                  className="w-11 h-11 rounded-full bg-yellow-400 hover:bg-yellow-300 text-[#0a101e] flex items-center justify-center transition-colors disabled:opacity-40"
                  aria-label={playing ? "Pause" : "Play"}
                >
                  {playing ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current ml-0.5" />}
                </button>
                <button onClick={goNext} disabled={!ready} className="text-slate-200 hover:text-yellow-400 transition-colors disabled:opacity-40" aria-label="Next">
                  <SkipForward className="w-5 h-5 fill-current" />
                </button>
              </div>

              <span className="flex items-center gap-1 text-[10px] text-yellow-400/70 uppercase tracking-wide">
                <Shuffle className="w-3 h-3" />
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MusicPlayer;
